/**
 * Phase 3: Performance Monitoring Service
 * Tracks load times, cache hit rates, and performance metrics
 */

interface PerformanceMetric {
  type: 'module_load' | 'audio_load' | 'cache_hit' | 'cache_miss' | 'navigation' | 'fcp' | 'lcp' | 'cls' | 'tti';
  timestamp: number;
  duration: number;
  context?: Record<string, any>;
}

class PerformanceMonitorServiceManager {
  private metrics: PerformanceMetric[] = [];
  private readonly MAX_METRICS = 100;
  private startTimes = new Map<string, number>();

  /**
   * Start timing an operation
   */
  startTimer(operationId: string): void {
    this.startTimes.set(operationId, performance.now());
  }

  /**
   * End timing and record metric
   */
  endTimer(
    operationId: string,
    type: PerformanceMetric['type'],
    context?: Record<string, any>
  ): number {
    const startTime = this.startTimes.get(operationId);
    if (!startTime) return 0;

    const duration = performance.now() - startTime;
    this.startTimes.delete(operationId);

    this.recordMetric(type, duration, context);
    return duration;
  }

  /**
   * Record a performance metric
   */
  recordMetric(
    type: PerformanceMetric['type'],
    duration: number,
    context?: Record<string, any>
  ): void {
    this.metrics.push({
      type,
      timestamp: Date.now(),
      duration,
      context,
    });

    // Keep only recent metrics
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics = this.metrics.slice(-this.MAX_METRICS);
    }

    // Log slow operations
    if (duration > 1000) {
      console.warn(`⚠️ [Performance] Slow ${type}: ${Math.round(duration)}ms`, context);
    }
  }

  /**
   * Get average load time for a metric type
   */
  getAverageLoadTime(type: PerformanceMetric['type']): number {
    const filtered = this.metrics.filter(m => m.type === type);
    if (filtered.length === 0) return 0;

    const sum = filtered.reduce((acc, m) => acc + m.duration, 0);
    return Math.round(sum / filtered.length);
  }

  /**
   * Get cache hit rate
   */
  getCacheHitRate(): number {
    const hits = this.metrics.filter(m => m.type === 'cache_hit').length;
    const misses = this.metrics.filter(m => m.type === 'cache_miss').length;
    const total = hits + misses;

    return total > 0 ? Math.round((hits / total) * 100) : 0;
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary() {
    return {
      avgModuleLoad: this.getAverageLoadTime('module_load'),
      avgAudioLoad: this.getAverageLoadTime('audio_load'),
      avgNavigation: this.getAverageLoadTime('navigation'),
      cacheHitRate: this.getCacheHitRate(),
      totalMetrics: this.metrics.length,
      recentMetrics: this.metrics.slice(-10),
    };
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    this.startTimes.clear();
  }

  /**
   * Log performance report to console
   */
  logReport(): void {
    const summary = this.getPerformanceSummary();
    console.log('📊 [Performance Report]', {
      'Module Load Time': `${summary.avgModuleLoad}ms`,
      'Audio Load Time': `${summary.avgAudioLoad}ms`,
      'Navigation Time': `${summary.avgNavigation}ms`,
      'Cache Hit Rate': `${summary.cacheHitRate}%`,
      'Total Metrics': summary.totalMetrics,
    });
  }

  /**
   * Phase 7: Track Core Web Vitals for world-class performance
   */
  trackWebVitals(): void {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
      return;
    }

    // First Contentful Paint (FCP)
    try {
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name === 'first-contentful-paint') {
            this.recordMetric('fcp', entry.startTime, {
              target: 1200, // Target: <1.2s
              pass: entry.startTime < 1200,
            });
            console.log(`🎨 [FCP] ${Math.round(entry.startTime)}ms ${entry.startTime < 1200 ? '✅' : '⚠️'}`);
          }
        });
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
    } catch (error) {
      console.warn('[Performance] FCP tracking failed:', error);
    }

    // Largest Contentful Paint (LCP)
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        if (lastEntry) {
          this.recordMetric('lcp', lastEntry.startTime, {
            target: 2500, // Target: <2.5s
            pass: lastEntry.startTime < 2500,
            element: lastEntry.element?.tagName,
          });
          console.log(`🖼️ [LCP] ${Math.round(lastEntry.startTime)}ms ${lastEntry.startTime < 2500 ? '✅' : '⚠️'}`);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (error) {
      console.warn('[Performance] LCP tracking failed:', error);
    }

    // Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const layoutShiftEntry = entry as any;
          if (!layoutShiftEntry.hadRecentInput) {
            clsValue += layoutShiftEntry.value;
          }
        }
        this.recordMetric('cls', clsValue, {
          target: 0.1, // Target: <0.1
          pass: clsValue < 0.1,
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (error) {
      console.warn('[Performance] CLS tracking failed:', error);
    }

    // Time to Interactive (TTI) - approximation
    this.measureTimeToInteractive();
  }

  /**
   * Measure Time to Interactive (approximation using load event)
   */
  private measureTimeToInteractive(): void {
    if (document.readyState === 'complete') {
      this.recordTTI();
    } else {
      window.addEventListener('load', () => this.recordTTI());
    }
  }

  private recordTTI(): void {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigationEntry) {
      const tti = navigationEntry.domInteractive;
      this.recordMetric('tti', tti, {
        target: 3500, // Target: <3.5s
        pass: tti < 3500,
      });
      console.log(`⚡ [TTI] ${Math.round(tti)}ms ${tti < 3500 ? '✅' : '⚠️'}`);
    }
  }

  /**
   * Phase 7: Check if performance budget is exceeded
   */
  checkPerformanceBudget(): void {
    const summary = this.getPerformanceSummary();
    const issues: string[] = [];

    // Budget: Module load <1s
    if (summary.avgModuleLoad > 1000) {
      issues.push(`Module load: ${summary.avgModuleLoad}ms (target: <1000ms)`);
    }

    // Budget: Audio load <2s
    if (summary.avgAudioLoad > 2000) {
      issues.push(`Audio load: ${summary.avgAudioLoad}ms (target: <2000ms)`);
    }

    // Budget: Navigation <200ms
    if (summary.avgNavigation > 200) {
      issues.push(`Navigation: ${summary.avgNavigation}ms (target: <200ms)`);
    }

    // Budget: Cache hit rate >90%
    if (summary.cacheHitRate < 90) {
      issues.push(`Cache hit rate: ${summary.cacheHitRate}% (target: >90%)`);
    }

    if (issues.length > 0) {
      console.warn('⚠️ [Performance Budget] EXCEEDED:');
      issues.forEach((issue) => console.warn(`  - ${issue}`));
    } else {
      console.log('✅ [Performance Budget] All targets met!');
    }
  }
}

export const performanceMonitor = new PerformanceMonitorServiceManager();
